import MainLayout from 'components/layout/main';
import FacilityPeriodFilter from './components/FacilityPeriodFilter';
import { useFacilityPeriodTable } from 'components/table/columns/facilityPeriod';
import { useLocation, useSearchParams } from 'react-router-dom';
import FacilityPeriodHeader from './components/FacilityPeriodHeader';
import FacilityPeriodStory from './components/FacilityPeriodStory';
import { useGetCurrentSelfEmployedTaxQuery } from 'pages/facilityGroupCasing/api/query/generated/GetCurrentSelfEmployedTax';
import React, { useState, useEffect } from 'react';
import { flexRender } from '@tanstack/react-table';
import clsx from 'clsx';
import DraggableColumnHeader from 'components/table/header/draggableColumnHeader';
import useFacilityBreadcrumbs from 'utils/hooks/breadcrumbs/useFacilityBreadcrumbs';
import CustomPagination from 'ui/pagination';

const mocksData = {
  id: 1,
  name: 'Родительский объект',
  status: 'active',
  erpId: 14177,
  author: {
    id: 1,
    firstname: 'Иван',
    lastname: 'Иванов',
    patronymic: 'Иванович',
  },
  sum: 500000,
  sumWithNpd: 678000,
  periodDate: '18.03.2025 — 01.04.2025',
  createdAt: '2021-01-01',
  executionDate: '2021-01-01',
  objects: [
    {
      id: '1',
      name: 'Ашан',
      users: [
        {
          id: '1',
          name: 'Абдуллаев М. Р',
          positions: [
            {
              id: '1',
              name: 'Работник торгового зала',
              totalShifts: 6,
              fact: '96 ч.',
              bonus: 3000,
              rate: 3500,
              sum: 21000,
              sumWithNpd: 18000,
              allowance: 6000,
              retention: 3000,
              status: 'rejected',
            },
            {
              id: '2',
              name: 'Кассир',
              totalShifts: 4,
              fact: '72 ч.',
              bonus: 2000,
              rate: 4000,
              sum: 14000,
              sumWithNpd: 12000,
              allowance: 4000,
              retention: 2000,
              status: 'active',
            },
          ],
        },
        {
          id: '2',
          name: 'Петров П. П.',
          positions: [
            {
              id: '3',
              name: 'Грузчик',
              totalShifts: 8,
              fact: '120 ч.',
              bonus: 3000,
              rate: 3000,
              sum: 24000,
              sumWithNpd: 20000,
              allowance: 8000,
              retention: 2000,
              status: 'active',
            },
          ],
        },
      ],
    },
    {
      id: '2',
      name: 'Магнит',
      users: [
        {
          id: '3',
          name: 'Сидоров С. С.',
          positions: [
            {
              id: '4',
              name: 'Менеджер',
              totalShifts: 12,
              fact: '180 ч.',
              bonus: 6000,
              rate: 4000,
              sum: 48000,
              sumWithNpd: 40000,
              allowance: 12000,
              retention: 3000,
              status: 'active',
            },
          ],
        },
      ],
    },
  ],
};

const FacilityPeriod: React.FC = () => {
  const location = useLocation();
  const [showHistory, setShowHistory] = useState(() => {
    const savedState = localStorage.getItem('facilityPeriod_showHistory');
    return savedState ? JSON.parse(savedState) : false;
  });

  useEffect(() => {
    localStorage.setItem('facilityPeriod_showHistory', JSON.stringify(showHistory));
  }, [showHistory]);

  const [searchParams] = useSearchParams();
  const [column, setColumn] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedProfession, setSelectedProfession] = useState<string | null>(null);

  const currentPage = Number(searchParams.get('page')) || 1;
  const pageSize = 10;

  const filteredData = React.useMemo(() => {
    let filtered = [...mocksData.objects];

    // Поиск по имени объекта или пользователя
    if (searchTerm && searchTerm.trim() !== '') {
      filtered = filtered
        .map(object => ({
          ...object,
          users: object.users.filter(
            user =>
              object.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
              user.name.toLowerCase().includes(searchTerm.toLowerCase()),
          ),
        }))
        .filter(object => object.users.length > 0);
    }

    // Фильтрация по статусу
    if (selectedStatus) {
      filtered = filtered
        .map(object => ({
          ...object,
          users: object.users
            .map(user => ({
              ...user,
              positions: user.positions.filter(pos => pos.status === selectedStatus),
            }))
            .filter(user => user.positions.length > 0),
        }))
        .filter(object => object.users.length > 0);
    }

    // Фильтрация по профессии
    if (selectedProfession) {
      filtered = filtered
        .map(object => ({
          ...object,
          users: object.users
            .map(user => ({
              ...user,
              positions: user.positions.filter(pos => pos.id === selectedProfession),
            }))
            .filter(user => user.positions.length > 0),
        }))
        .filter(object => object.users.length > 0);
    }

    return filtered;
  }, [searchTerm, selectedStatus, selectedProfession]);

  const totalPages = Math.ceil(filteredData.length / pageSize);

  const facilityBreadCrumbs = useFacilityBreadcrumbs();

  const { data: currentSelfTaxData } = useGetCurrentSelfEmployedTaxQuery();
  const selfEmployedTaxRate = currentSelfTaxData?.getCurrentSelfEmployedTax || 0.93;

  const periodTitle = `Период на выплату №${mocksData.erpId}`;
  const updatedBreadCrumbs = facilityBreadCrumbs
    ? [...facilityBreadCrumbs, { name: periodTitle, path: location.pathname }]
    : [];

  const table = useFacilityPeriodTable(filteredData);

  const renderTable = () => {
    if (filteredData.length === 0) {
      return (
        <div className="flex justify-center my-5">
          <h3 className="text-smena_text Body1">По вашему запросу ничего не нашлось</h3>
        </div>
      );
    }

    return (
      <div className="overflow-auto h-[calc(100vh-430px)]">
        <table
          {...{
            style: {
              width: table.getCenterTotalSize(),
            },
          }}
        >
          <thead>
            {table.getHeaderGroups().map(headerGroup => (
              <tr key={headerGroup.id}>
                {headerGroup.headers.map(header => (
                  <DraggableColumnHeader
                    key={header.id}
                    header={header}
                    table={table}
                    hoverColumn={column}
                    setHoverColumn={setColumn}
                  />
                ))}
              </tr>
            ))}
          </thead>
          <tbody>
            {table.getRowModel().rows.map(row => (
              <React.Fragment key={row.id}>
                <tr
                  className={clsx('table__row', row.depth === 0 ? 'bg-smena_gray-5' : '')}
                  onClick={() => {
                    if (row.depth !== 0 && row.depth === 1 && row.original.positions?.length > 1) {
                      row.toggleExpanded();
                    }
                  }}
                  style={{
                    cursor:
                      row.depth !== 0 && row.depth === 1 && row.original.positions?.length > 1 ? 'pointer' : 'default',
                  }}
                >
                  {row.getVisibleCells().map((cell, index) => {
                    if (row.depth === 0 && index > 0) {
                      return null;
                    }
                    return (
                      <td
                        key={cell.id}
                        className={clsx(
                          'table__content',
                          column === cell.column.id ? 'active-column' : '',
                          row.depth === 0 && index === 0 ? 'col-span-full' : '',
                        )}
                        colSpan={row.depth === 0 && index === 0 ? table.getAllColumns().length : 1}
                        onMouseEnter={() => {
                          setColumn(cell.column.id);
                        }}
                        onMouseLeave={() => {
                          setColumn('');
                        }}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    );
                  })}
                </tr>
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>
    );
  };

  return (
    <MainLayout title={periodTitle} breadCrumbs={updatedBreadCrumbs}>
      <FacilityPeriodHeader
        period={mocksData}
        selfEmployedTaxRate={selfEmployedTaxRate}
        showHistory={showHistory}
        toggleHistoryVisible={() => setShowHistory((prevState: boolean) => !prevState)}
      />
      <div className={clsx('grid gap-6', showHistory ? 'grid-cols-[1fr_540px]' : 'grid-cols-[1fr]')}>
        <div className="flex flex-col shadow-smena rounded bg-smena_white overflow-hidden relative">
          <div className="relative z-10">
            <FacilityPeriodFilter
              table={table}
              positions={mocksData.objects[0].users[0].positions}
              onSearchChange={setSearchTerm}
              onStatusChange={setSelectedStatus}
              onProfessionChange={setSelectedProfession}
            />
          </div>
          <div className="flex-1 overflow-auto">{renderTable()}</div>
          <CustomPagination currentPage={currentPage} total={totalPages} />
        </div>
        {showHistory && <FacilityPeriodStory />}
      </div>
    </MainLayout>
  );
};

export default FacilityPeriod;
